import { IconDotsVertical } from "@tabler/icons-react";
import type { ButtonProps, MenuProps } from "antd";
import { Button, Dropdown, Flex, Spin, Tooltip, Typography } from "antd";
import Compact from "antd/es/space/Compact";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
	type ActionItem,
	type ActionVariant,
	type ButtonAction,
	type CustomAction,
	type SubmenuAction,
	useActions,
} from "@/contexts/actions-context";
import { CustomIcon } from "../../base";
import { CustomDrawer } from "./custom-drawer";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COLLAPSE_WIDTH = 768;

type ButtonType = ButtonProps["type"];

const antType: Record<ActionVariant, ButtonType> = {
	primary: "primary",
	outlined: "default",
	text: "text",
	default: "default",
};

// ─── Desktop renderers ───────────────────────────────────────────────────────

function renderButton(action: ButtonAction): ReactNode {
	return (
		<Tooltip key={action.id} title={action.tooltip} placement="bottom">
			<Button
				key={action.id}
				type={antType[action.variant ?? "outlined"]}
				disabled={action.disabled}
				onClick={action.onClick}
				icon={action.icon}
				danger={action.danger}
			>
				{action.label}
			</Button>
		</Tooltip>
	);
}

function renderSubmenu(action: SubmenuAction): ReactNode {
	const items: MenuProps["items"] = action.submenu.map((sub) => ({
		key: sub.id,
		label: (
			<span
				key={action.id}
				style={{ display: "flex", alignItems: "center", gap: 6 }}
			>
				{sub.icon}
				{sub.label}
			</span>
		),
		disabled: sub.disabled,
		onClick: sub.onClick,
		danger: sub.danger,
	}));

	return (
		<Dropdown key={action.id} menu={{ items }} placement="bottom">
			<Button
				type={antType[action.variant ?? "outlined"]}
				disabled={action.disabled}
				icon={action.icon}
			>
				{action.label}
			</Button>
		</Dropdown>
	);
}

function renderAction(action: ActionItem): ReactNode {
	switch (action.type) {
		case "button":
			return renderButton(action);
		case "submenu":
			return renderSubmenu(action);
		case "custom":
			return <span key={action.id}>{action.node}</span>;
		case "divider":
			return (
				<div
					key={action.id}
					style={{
						width: 1,
						height: 18,
						background: "var(--ant-color-border)",
					}}
				/>
			);
	}
}

// ─── Mobile drawer ───────────────────────────────────────────────────────────

/** Flatten buttons and submenus; exclude customs and dividers */
function flattenButtons(actions: ActionItem[]): ButtonAction[] {
	return actions.flatMap((action) => {
		switch (action.type) {
			case "button":
				return [action];
			case "submenu":
				return action.submenu;
			default:
				return [];
		}
	});
}

function CollapsedDrawer({
	actions,
	open,
	onClose,
}: {
	actions: ActionItem[];
	open: boolean;
	onClose: () => void;
}) {
	const customItems = actions.filter(
		(a): a is CustomAction => a.type === "custom",
	);
	const buttons = flattenButtons(actions);

	return (
		<CustomDrawer
			placement="bottom"
			open={open}
			onClose={onClose}
			styles={{ body: { padding: 16 } }}
			title="Actions"
			closable
		>
			{/* Custom nodes stacked on top, full width */}
			{customItems.length > 0 && (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 8,
						marginBottom: buttons.length > 0 ? 16 : 0,
					}}
				>
					{customItems.map((a) => (
						<div key={a.id}>{a.collapsedNode ?? a.node}</div>
					))}
				</div>
			)}

			{/* Buttons centered; each shows a label (falls back to tooltip for icon-only actions) */}
			{buttons.length > 0 && (
				<Flex gap={8} wrap="wrap" justify="center">
					{buttons.map((action) => (
						<Button
							key={action.id}
							type={antType[action.variant ?? "default"]}
							disabled={action.disabled}
							icon={action.icon}
							onClick={() => {
								action.onClick();
								onClose();
							}}
						>
							{action.label ?? action.tooltip}
						</Button>
					))}
				</Flex>
			)}
		</CustomDrawer>
	);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ActionsBar() {
	const { actions, busy } = useActions();
	const [collapsed, setCollapsed] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el =
			ref.current?.closest(".ant-layout-content")?.parentElement ??
			document.body;
		const observer = new ResizeObserver(([entry]) => {
			setCollapsed(entry.contentRect.width < COLLAPSE_WIDTH);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	if (actions.length === 0 && !busy) return null;

	const busyNode = busy && (
		<Flex align="center" gap={6}>
			<Spin size="small" />
			<Typography.Text type="secondary">{busy.message}</Typography.Text>
		</Flex>
	);

	if (collapsed) {
		return (
			<Flex align="center" gap={8}>
				{busyNode}
				{actions.length > 0 && (
					<>
						<Button
							type="text"
							icon={
								<CustomIcon icon={IconDotsVertical} size={18} stroke={1.5} />
							}
							onClick={() => setDrawerOpen(true)}
						/>
						<CollapsedDrawer
							actions={actions}
							open={drawerOpen}
							onClose={() => setDrawerOpen(false)}
						/>
					</>
				)}
			</Flex>
		);
	}

	return (
		<Flex align="center" gap={12} ref={ref}>
			{busyNode}
			<Compact>{actions.map(renderAction)}</Compact>
		</Flex>
	);
}
